<template>
  <v-app>
    <v-main class="ma-6 pa-0">
      <v-container class="pa-0" style="max-width: 600px">
        <!-- Title and sub-title -->
        <div>
          <h1 class="heading-1" id="heading">
            {{ $t("text." + actualScreen + ".heading") }}
          </h1>
          <p class="body ma-0">
            {{ $t("text." + actualScreen + ".body") }}
          </p>
        </div>
        <!-- Notification -->
        <VNotification
          :show="notification.show"
          :title="notification.title"
          :text="notification.text"
          :variant="notification.variant"
        />
        <!-- Loading spinner -->
        <v-col
          cols="12"
          v-if="reqStatus === 'PENDING'"
          class="pt-0 d-flex justify-center"
        >
          <scale-loading-spinner
            text="Saving data..."
            size="large"
          ></scale-loading-spinner>
        </v-col>
        <!-- Data Form -->
        <TheUserDataForm
          ref="userDataForm"
          v-if="actualScreen === 'mainScreen'"
          :actual-screen="actualScreen"
          @screen-change="changeScreen"
          @data-updated="getRequestStatus"
          @notify="showNotification"
        />
        <!-- Email Form -->
        <TheEmailChangeForm
          ref="emailChangeForm"
          v-else
          :actual-screen="actualScreen"
          @screen-change="changeScreen"
          @email-updated="getRequestStatus"
          @notify="showNotification"
        />
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import TheEmailChangeForm from './components/TheEmailChangeForm.vue';
import TheUserDataForm from './components/TheUserDataForm.vue';
import VNotification from './components/VNotification.vue';
import PersonAPI from './services/PersonAPI';
import packageInfo from '../package.json';

const { t } = useI18n();
const keycloak = inject('keycloak');

const actualScreen = ref('mainScreen');
const notification = ref({
  show: false,
  title: '',
  text: [],
  variant: 'informational'
});
const reqStatus = ref('');
const reqTimeout = ref(60); // 60 * 0.5s = 30s timeout
const userDataForm = ref (null);

function showNotification(variant, title, text) {
  notification.value.show = false;
  if (text !== undefined && !Array.isArray(text)) {
    text = [text];
  }
  notification.value = {
    show: true,
    title: title,
    variant: variant,
    text: text
  };
  if (variant === 'success') {
    setTimeout(() => {
      notification.value.show = false;
    }, 5000);
  }
}

function changeScreen(screenName) {
  actualScreen.value = screenName;
  notification.value.show = false;
}

function getRequestStatus(sessionId) {
  PersonAPI.getStatusOfUpdate(keycloak.token, sessionId) // You may need to pass the token here
    .then((res) => {
      reqStatus.value = res.data.status;
      switch (reqStatus.value) {
        case 'PENDING': {
          setTimeout(() => {
            if (reqTimeout.value > 0) {
              reqTimeout.value--;
              getRequestStatus(sessionId);
            } else {
              reqTimeout.value = 60;
              showNotification('error', 'Timeout', '');
            }
          }, 500);
          break;
        }
        case 'SUCCESS': {
          if (userDataForm.value !== undefined) {
            userDataForm.value.cancelEditing();
          }
          changeScreen('mainScreen');
          showNotification('success', t('notificationMsg.update.success'));
          break;
        }
        case 'FAILED': {
          if (userDataForm.value !== undefined) {
            userDataForm.value.cancelEditing();
          }
          changeScreen('mainScreen');
          showNotification('error', 'Request failed (API problem)', '');
          break;
        }
        case 'ERROR': {
          if (userDataForm.value !== undefined) {
            userDataForm.value.cancelEditing();
          }
          changeScreen('mainScreen');
          showNotification('error', t('notificationMsg.update.error'), res.data.errorMsg);
          break;
        }
        default:
          break;
      }
    })
    .catch((err) => {
      console.error('Error getting status -> ', err);
      if (err.response.error !== undefined) {
        showNotification('error', err.response.error);
      } else {
        showNotification('error', `${err.response.status} ${err.response.statusText}`);
      }
    });
}

onMounted(() => {
  console.log('User self-service ' + packageInfo.version);
});
</script>

<style lang="scss">
#app {
  font-family: var(--scl-font-family-sans);
}
.heading-1 {
  font: var(--scl-font-variant-heading-3-weight)
    (
      var(--scl-font-variant-heading-3-size) + "/" +
        var(--scl-font-variant-heading-3-line-height)
    )
    var(--scl-font-variant-heading-3-family);
}
.body {
  font: var(--scl-font-weight-thin)
    (
      var(--scl-font-variant-heading-3-size) + "/" +
        var(--scl-font-variant-heading-1-line-height)
    )
    var(--scl-font-variant-heading-3-family);
}
scale-text-field {
  line-height: initial;
}
</style>
